var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
/* eslint-disable max-len */
import _ from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from '@/hooks/lib/react-router/useNavigate';
import { apiCreateClientSeminarRequest } from '../../../actions/ClientSeminarActions';
import { clearFinalFormValues, updateFinalFormValues, } from '../../../actions/FinalFormActions';
import { apiGetOneProfile } from '../../../actions/ProfileActions';
import SeminarOneForm from '../../../container/Seminar/One/Parts/SeminarOneForm';
import FinalFormSpy from '../../../container/Ui/Common/FinalForm/FinalFormSpy';
import { OCCUPATION_CANDIDATES, POSITION_CANDIDATES, } from '../../../define/consts/profile';
import { notify, notifyError, wrapDispatch } from '../../action';
import { isGuest } from '../../auth';
import { sleep } from '../../common';
import { finalFormValidateScroll } from '../../form';
import { createFinalFormValidateErrors } from '../../validate';
import { usePromiseDoingCallback } from '../useHook';
import { useKanaRef } from '../useKana';
// フォーム名
const formName = 'ClientSeminarOneRequestForm';
// フォームの値を整形する
const parseFormValues = (formValues) => _.pick(formValues, [
    'firstName',
    'firstNameKana',
    'lastName',
    'lastNameKana',
    'email',
    'phone1',
    'phone2',
    'phone3',
    'companyName',
    'occupationOther',
    'positionOther',
    'departmentName',
    'description',
]);
/**
 * セミナー申し込みの編集を行うuse
 * @param options
 * @returns {*}
 */
export const useClientSeminarRequestEdit = (options) => {
    const { dispatch, profile, seminar, unmountRef } = options;
    const history = useNavigate();
    const [formSpyState, changeFormSpyState] = useState({});
    const auth = useSelector((state) => state.auth);
    const values = useSelector((state) => state.finalForm.form[formName]);
    const isGuestUser = isGuest(auth);
    const kanaRef = useKanaRef();
    const { id } = seminar;
    /**
     * フォームの初期値
     * @type {*}
     */
    const [initialValues] = useState(() => {
        if (values) {
            // 入力値が存在する場合は復元する
            return values;
        }
        return profile && profile.information
            ? Object.assign({ companyName: profile.organization ? profile.organization.name : '', departmentName: profile.information.department || '', email: profile.email, occupation: OCCUPATION_CANDIDATES.find((v) => v.value === parseInt(profile.information.occupation, 10)), position: POSITION_CANDIDATES.find((v) => v.value === parseInt(profile.information.position, 10)) }, _.pick(profile.information, [
                'lastName',
                'lastNameKana',
                'firstName',
                'firstNameKana',
                'phone1',
                'phone2',
                'phone3',
                'positionOther',
                'occupationOther',
            ])) : {};
    });
    /**
     * 確認ページへsubmit
     * @type {*}
     */
    const onSubmit = useCallback((formValues, formProps, callback) => __awaiter(void 0, void 0, void 0, function* () {
        const kana = {
            firstNameKana: kanaRef.current.first.getFurigana(),
            lastNameKana: kanaRef.current.last.getFurigana(),
        };
        const params = Object.assign(Object.assign(Object.assign({}, parseFormValues(formValues)), kana), { occupation: formValues.occupation.value, position: formValues.position.value });
        // 申し込みのバリデーションを実行
        try {
            yield wrapDispatch(dispatch, apiCreateClientSeminarRequest(id, params, { isValidateOnly: true }));
        }
        catch (e) {
            if (!unmountRef.current) {
                const errors = createFinalFormValidateErrors(e);
                callback(errors);
                finalFormValidateScroll(errors);
                return;
            }
        }
        if (unmountRef.current) {
            return;
        }
        // フォームの値を保持する
        dispatch(updateFinalFormValues(formName, Object.assign(Object.assign({}, formValues), kana)));
        yield sleep(0.3);
        // 確認ページへ飛ばす
        if (!unmountRef.current) {
            history.push(`/seminar/${id}/request/confirm`, { state: { seminar } });
        }
    }), [history, dispatch, id, kanaRef, seminar, unmountRef]);
    /**
     * 入力フォームを描画
     * @type {*}
     */
    const renderForm = useCallback((renderProps) => (_jsxs(_Fragment, { children: [_jsx(SeminarOneForm, Object.assign({}, renderProps, { isGuestUser: isGuestUser, kanaRef: kanaRef, profile: profile, seminar: seminar })), _jsx(FinalFormSpy, { onChange: changeFormSpyState })] })), [isGuestUser, kanaRef, profile, seminar]);
    // 確認ページ以外に遷移する場合はフォームの値を初期化する
    useEffect(() => () => {
        if (window.location.pathname !== `/seminar/${id}/request/confirm`) {
            dispatch(clearFinalFormValues(formName));
        }
    }, [id, dispatch]);
    return {
        formSpyState,
        initialValues,
        onSubmit,
        renderForm,
    };
};
/**
 * セミナー申し込み確認ページの制御を行うuse
 * @param options
 * @returns {*}
 */
export const useClientSeminarRequestConfirm = (options) => {
    const { dispatch, seminar, unmountRef, values } = options;
    const history = useNavigate();
    const auth = useSelector((state) => state.auth);
    const isGuestUser = isGuest(auth);
    const { id } = seminar;
    /**
     * 申し込みを実行する
     * @type {(function(*): Promise<void>)|*}
     */
    const rawOnSubmit = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        // 入力値の整形
        const params = Object.assign(Object.assign({}, parseFormValues(values)), { occupation: values.occupation.value, position: values.position.value });
        // 申し込みを実行
        try {
            yield wrapDispatch(dispatch, apiCreateClientSeminarRequest(id, params));
        }
        catch (e) {
            if (!unmountRef.current) {
                // エラーの場合は前のページに戻す
                notifyError();
                history.push(`/seminar/${id}/request`);
                return;
            }
        }
        if (unmountRef.current) {
            return;
        }
        // ログイン状態であればプロフィールを引き直す
        if (!isGuestUser) {
            yield wrapDispatch(dispatch, apiGetOneProfile());
        }
        if (unmountRef.current) {
            return;
        }
        notify('問い合わせを完了しました');
        // 完了ページに飛ばす
        history.replace(`/seminar/${id}/request/complete`, { state: { seminar } });
    }), [history, values, dispatch, id, isGuestUser, unmountRef, seminar]);
    const [onSubmit, loading] = usePromiseDoingCallback(rawOnSubmit, unmountRef);
    // 編集ページ以外に遷移する場合はフォームの値を初期化する
    useEffect(() => () => {
        if (window.location.pathname !== `/seminar/${id}/request`) {
            dispatch(clearFinalFormValues(formName));
        }
    }, [id, dispatch]);
    return {
        loading,
        onSubmit,
    };
};
